import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import PoweredBy from "@/components/PoweredBy";

export default function Diagnostic() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    client.get("/diagnostic/questions").then(({ data }) => setQuestions(data.questions));
  }, []);

  if (!questions.length) {
    return <div className="grid min-h-screen place-items-center bg-[#FAF9F6]"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-[var(--tenant-primary)]" /></div>;
  }

  const q = questions[step];
  const total = questions.length;
  const selected = answers[q.id];
  const pct = Math.round(((step) / total) * 100);

  const choose = (val) => setAnswers({ ...answers, [q.id]: val });

  const next = async () => {
    if (step < total - 1) { setStep(step + 1); return; }

    // Diagnostic-first flow: if the visitor isn't signed in yet, stash their
    // answers and send them to create an account. We'll apply the answers
    // right after signup so their roadmap is ready on the dashboard.
    if (!localStorage.getItem("fisbo_token")) {
      localStorage.setItem("fisbo_pending_diagnostic", JSON.stringify(answers));
      navigate("/register");
      return;
    }

    setSubmitting(true);
    try {
      await client.post("/diagnostic/submit", { answers });
      toast.success("Your roadmap is ready!");
      navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        localStorage.setItem("fisbo_pending_diagnostic", JSON.stringify(answers));
        localStorage.removeItem("fisbo_token");
        navigate("/register");
        return;
      }
      const detail = err?.response?.data?.detail;
      const msg = detail
        ? formatApiError(detail)
        : "We couldn't reach the server. Check your connection and tap the button again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-4 py-5"><Logo /><span className="text-sm text-neutral-500" data-testid="diagnostic-progress-label">Question {step + 1} of {total}</span></header>
      <PoweredBy className="mx-auto max-w-3xl px-4" />
      <div className="mx-auto max-w-3xl px-4 pb-16 pt-4">
        <div className="mb-10 h-2 rounded-full bg-neutral-200">
          <div className="h-2 rounded-full tenant-bg transition-all duration-500" style={{ width: `${pct}%` }} data-testid="diagnostic-progress-bar" />
        </div>

        <div key={q.id} className="fade-up rounded-2xl border border-[#E5E2DC] bg-white p-8 sm:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Meeting you where you are</p>
          <h1 className="mt-3 font-head text-2xl font-semibold leading-snug text-neutral-900 sm:text-3xl" data-testid="diagnostic-question">{q.prompt}</h1>
          <p className="mt-3 text-neutral-600">{q.help}</p>

          <div className="mt-8 space-y-3">
            {q.options.map((opt) => {
              const active = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`option-${opt.value}`}
                  onClick={() => choose(opt.value)}
                  className={`flex w-full items-center justify-between rounded-xl border px-5 py-4 text-left transition-all duration-200 ${active ? "tenant-border tenant-light-bg" : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"}`}
                >
                  <span className={`text-base ${active ? "font-semibold text-neutral-900" : "text-neutral-700"}`}>{opt.label}</span>
                  <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border ${active ? "tenant-bg border-transparent" : "border-neutral-300"}`}>
                    {active && <Check size={14} className="text-white" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button data-testid="diagnostic-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-40">
            <ArrowLeft size={16} /> Back
          </button>
          <button data-testid="diagnostic-next" onClick={next} disabled={!selected || submitting} className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-40" style={{ backgroundColor: "var(--tenant-primary)" }}>
            {submitting ? "Building your roadmap..." : step === total - 1 ? "See my roadmap" : "Continue"} {!submitting && <ArrowRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
